/**
 * clusters.ts - Análisis de Clusters y Validación Cruzada Completa
 * 
 * Módulo extraído de crossValidation.ts (TD-002.G.6)
 * Implementa el análisis de clusters, score compuesto, detección
 * de discrepancias y la función principal performCrossValidation.
 * 
 * @created 2026-01-26
 */

import {
  createErrorContext,
  createError,
  getRecommendedAction,
  applyErrorPenalties,
  huberCentroidFromSources,
  type GeocodingError,
} from '../../types/errors';

import {
  DEFAULT_CROSS_VALIDATION_LEVEL,
  type UTMPoint as _UTMPoint,
  type GeocodingSourceId,
  type GeocodingSource,
  type GeocodingQuery,
  type SourceResult,
  type ClusterAnalysis,
  type CrossValidationResult,
} from './types';

// Re-export para que los tests puedan importar ClusterAnalysis desde clusters directamente
export type { ClusterAnalysis } from './types';

import { distanceUTM as _distanceUTM } from './geometry';
import { queryMultipleSources, getSuccessfulResults, extractCoordinates as _extractCoordinates } from './sources';
import { calculateCrossNameScore } from './nameComparison';
import { huberCentroid, identifyOutliers, calculateClusterRadius, calculateConcordanceScore } from './statistics';

// ============================================================================
// CONSTANTES EXPORTADAS
// ============================================================================

/** Pesos de autoridad por fuente (claves canónicas lowercase) */
export const SOURCE_AUTHORITY_WEIGHTS: Partial<Record<GeocodingSourceId, number>> = {
  local_dera: 0.95,      // Datos DERA offline - máxima autoridad
  wfs_sicess: 0.90,      // WFS especializado sanitario
  wfs_education: 0.90,
  wfs_iaph: 0.85,
  wfs_security: 0.85,
  cartociudad: 0.80,     // IGN CartoCiudad
  cdau: 0.80,            // Callejero Andalucía
  nomenclator: 0.75,     // Nomenclátor Geográfico
  nominatim: 0.55,       // OpenStreetMap Nominatim
  // Fuentes Cloudflare R2
  dera_r2: 0.92,          // DERA G12 servicios - alta autoridad
  dera_g10_r2: 0.90,      // DERA G10 energía/medioambiente
  dera_g3_r2: 0.90,       // DERA G3 depósitos agua
  dera_viaria_r2: 0.70,   // DERA viaria centroides (menor precisión)
  nga_r2: 0.78,           // NGA topónimos
  cdau_r2: 0.82,          // CDAU callejero
  catastro_r2: 0.85,      // Catastro inmuebles oficiales
  agol_r2: 0.80,          // ArcGIS Online
  iaph_r2: 0.85,          // IAPH patrimonio cultural
  cgpha_r2: 0.87,         // CGPHA patrimonio histórico catalogado
  obph_r2: 0.80,          // OBPH otros bienes patrimonio histórico
  ine_r2: 0.88,           // INE códigos municipales oficiales
  // OPT-03: Oleada 3
  idee: 0.78,          // IDEE/IGN OGC Features API
  catastro_api: 0.82,  // Catastro REST API directa
};

/** Umbrales de distancia (metros) por tipología para detectar discrepancias */
export const DISCREPANCY_THRESHOLDS: Record<string, number> = {
  // Infraestructuras puntuales - umbral bajo
  SANITARIO: 50,
  EDUCATIVO: 50,
  SEGURIDAD: 50,
  SERVICIOS_BASICOS: 75,
  
  // Infraestructuras de área - umbral medio
  DEPORTIVO: 100,
  CULTURAL: 100,
  ADMINISTRATIVO: 75,
  
  // Infraestructuras lineales/extensas - umbral alto
  TRANSPORTE: 150,
  HIDRAULICO: 200,
  ENERGIA: 200,

  // M6: Tipologías de área/polígono - umbrales amplios (DEFAULT=100 insuficiente)
  INDUSTRIAL: 300,    // Polígonos industriales: fuentes pueden diferir hasta 300m
  COMBUSTIBLE: 250,   // Gasolineras/depósitos: área de servicio amplia
  VIAL: 400,          // Infraestructura viaria: trazado lineal, alta dispersión
  RSU: 350,           // Residuos sólidos urbanos: plantas/vertederos extensos
  TELECOM: 200,       // Torres/antenas: cobertura de área

  // Default para tipologías no especificadas
  DEFAULT: 100
};

/** Pesos para el score compuesto */
const COMPOSITE_WEIGHTS = {
  /** Peso del match de nombre (0-1) */
  matchScore: 0.35,
  /** Peso de la concordancia entre fuentes (0-1) */
  concordanceScore: 0.40,
  /** Peso de la autoridad de la fuente principal (0-1) */
  authorityScore: 0.25
};

/** Umbral por defecto para concordancia (metros) */
const DEFAULT_CONCORDANCE_THRESHOLD_METERS = 100;

// ============================================================================
// FUNCIONES AUXILIARES (privadas)
// ============================================================================

// mapToTypedSource() eliminada en TD-010.1: GeocodingSourceId ya es canónico lowercase

// ============================================================================
// SCORE COMPUESTO
// ============================================================================

/**
 * Calcula el score compuesto de validación cruzada.
 * 
 * Fórmula: α×matchScore + β×concordanceScore + γ×authorityScore
 * donde α=0.35, β=0.40, γ=0.25
 * 
 * @param matchScore - Score de coincidencia de nombre (0-100)
 * @param concordanceScore - Score de concordancia entre fuentes (0-1)
 * @param authorityScore - Peso de autoridad promedio de fuentes concordantes (0-1)
 * @returns Score compuesto (0-100)
 */
export function calculateCompositeScore(
  matchScore: number,
  concordanceScore: number,
  authorityScore: number
): number {
  // Normalizar matchScore a 0-1 si viene en escala 0-100
  const normalizedMatch = matchScore > 1 ? matchScore / 100 : matchScore;
  
  const composite = 
    COMPOSITE_WEIGHTS.matchScore * normalizedMatch +
    COMPOSITE_WEIGHTS.concordanceScore * concordanceScore +
    COMPOSITE_WEIGHTS.authorityScore * authorityScore;
  
  // Devolver en escala 0-100
  return Math.round(composite * 100);
}

// ============================================================================
// DETECCIÓN DE DISCREPANCIAS
// ============================================================================

/**
 * Obtiene el umbral de discrepancia para una tipología.
 * 
 * @param tipologia - Tipología PTEL
 * @returns Umbral en metros
 */
export function getDiscrepancyThreshold(tipologia?: string): number {
  if (!tipologia) return DISCREPANCY_THRESHOLDS.DEFAULT;
  
  const upper = tipologia.toUpperCase();
  return DISCREPANCY_THRESHOLDS[upper] ?? DISCREPANCY_THRESHOLDS.DEFAULT;
}

/**
 * Detecta si hay discrepancia significativa entre fuentes.
 * 
 * Una discrepancia existe cuando:
 * - El radio del cluster excede el umbral para la tipología
 * - O hay outliers con peso alto (fuentes autoritativas discrepantes)
 * 
 * @param clusterAnalysis - Análisis del cluster
 * @param tipologia - Tipología de la infraestructura
 * @returns Objeto con detección y razón
 */
export function detectDiscrepancy(
  clusterAnalysis: ClusterAnalysis,
  tipologia?: string
): { hasDiscrepancy: boolean; reason: string; severity: 'low' | 'medium' | 'high' } {
  const threshold = getDiscrepancyThreshold(tipologia);
  
  // Verificar radio del cluster
  if (clusterAnalysis.radiusMeters > threshold * 2) {
    return {
      hasDiscrepancy: true,
      reason: `Radio del cluster (${Math.round(clusterAnalysis.radiusMeters)}m) excede umbral crítico (${threshold * 2}m)`,
      severity: 'high'
    };
  }
  
  if (clusterAnalysis.radiusMeters > threshold) {
    return {
      hasDiscrepancy: true,
      reason: `Radio del cluster (${Math.round(clusterAnalysis.radiusMeters)}m) excede umbral (${threshold}m)`,
      severity: 'medium'
    };
  }
  
  // Verificar outliers de fuentes autoritativas
  const authoritativeOutliers = clusterAnalysis.outlierSources.filter(
    id => (SOURCE_AUTHORITY_WEIGHTS[id] ?? 0) >= 0.80
  );
  
  if (authoritativeOutliers.length > 0) {
    return {
      hasDiscrepancy: true,
      reason: `Fuentes autoritativas discrepantes: ${authoritativeOutliers.join(', ')}`,
      severity: 'medium'
    };
  }
  
  // Verificar concordancia baja
  if (clusterAnalysis.concordanceScore < 0.6) {
    return {
      hasDiscrepancy: true,
      reason: `Concordancia baja entre fuentes (${Math.round(clusterAnalysis.concordanceScore * 100)}%)`,
      severity: 'low'
    };
  }
  
  return {
    hasDiscrepancy: false,
    reason: 'Todas las fuentes concuerdan dentro del umbral',
    severity: 'low'
  };
}

// ============================================================================
// RECOMENDACIONES
// ============================================================================

/**
 * Genera una recomendación basada en el análisis de validación cruzada.
 * 
 * @param compositeScore - Score compuesto (0-100)
 * @param discrepancy - Resultado de detección de discrepancia
 * @param concordantSources - Número de fuentes que concuerdan
 * @returns Recomendación y razón
 */
export function generateRecommendation(
  compositeScore: number,
  discrepancy: { hasDiscrepancy: boolean; severity: 'low' | 'medium' | 'high' },
  concordantSources: number
): { recommendation: CrossValidationResult['recommendation']; reason: string } {
  
  // REJECT: Score muy bajo o discrepancia severa
  if (compositeScore < 40) {
    return {
      recommendation: 'REJECT',
      reason: `Score muy bajo (${compositeScore}%) - datos insuficientes o conflictivos`
    };
  }
  
  if (discrepancy.hasDiscrepancy && discrepancy.severity === 'high') {
    return {
      recommendation: 'REJECT',
      reason: 'Discrepancia severa entre fuentes - requiere verificación manual antes de uso'
    };
  }
  
  // MANUAL_REVIEW: Score medio o discrepancia moderada
  if (compositeScore < 70) {
    return {
      recommendation: 'MANUAL_REVIEW',
      reason: `Score moderado (${compositeScore}%) - revisar coordenadas manualmente`
    };
  }
  
  if (discrepancy.hasDiscrepancy && discrepancy.severity === 'medium') {
    return {
      recommendation: 'MANUAL_REVIEW',
      reason: 'Discrepancia moderada - verificar ubicación en mapa'
    };
  }
  
  if (concordantSources === 0) {
    return {
      recommendation: 'MANUAL_REVIEW',
      reason: 'Sin fuentes concordantes — coordenada no verificada, revisar manualmente'
    };
  }

  if (concordantSources < 2) {
    return {
      recommendation: 'MANUAL_REVIEW',
      reason: 'Solo una fuente disponible - confirmar con otra fuente si es posible'
    };
  }
  
  // USE_RESULT: Score alto y sin discrepancias significativas
  if (compositeScore >= 85 && !discrepancy.hasDiscrepancy) {
    return {
      recommendation: 'USE_RESULT',
      reason: `Alta confianza (${compositeScore}%) - ${concordantSources} fuentes concuerdan`
    };
  }
  
  // Caso intermedio: score bueno pero con alguna incertidumbre
  if (compositeScore >= 70) {
    if (discrepancy.hasDiscrepancy && discrepancy.severity === 'low') {
      return {
        recommendation: 'USE_RESULT',
        reason: `Confianza aceptable (${compositeScore}%) con discrepancia menor`
      };
    }
    return {
      recommendation: 'USE_RESULT',
      reason: `Confianza buena (${compositeScore}%) - ${concordantSources} fuentes concuerdan`
    };
  }
  
  // Default: revisar manualmente
  return {
    recommendation: 'MANUAL_REVIEW',
    reason: 'Revisar coordenadas manualmente por precaución'
  };
}

// ============================================================================
// ANÁLISIS DE CLUSTERS
// ============================================================================

/**
 * Analiza el cluster de resultados de geocodificación.
 * 
 * Esta función:
 * 1. Extrae coordenadas de resultados exitosos
 * 2. Calcula centroide robusto (Huber)
 * 3. Identifica outliers
 * 4. Calcula radio del cluster
 * 5. Calcula score de concordancia
 * 
 * @param results - Array de resultados de múltiples fuentes
 * @param options - Opciones de configuración
 * @returns Análisis del cluster o null si no hay suficientes datos
 * 
 * @example
 * const analysis = analyzeResultClusters(sourceResults);
 * if (analysis && analysis.concordanceScore > 0.8) {
 *   console.log('Coordenada confiable:', analysis.centroid);
 * }
 */
export function analyzeResultClusters(
  results: SourceResult[],
  options: {
    /** Umbral de distancia para concordancia (metros) */
    concordanceThreshold?: number;
  } = {}
): ClusterAnalysis | null {
  const { 
    concordanceThreshold = DEFAULT_CONCORDANCE_THRESHOLD_METERS 
  } = options;
  
  // Filtrar resultados exitosos
  const successful = getSuccessfulResults(results);
  
  if (successful.length === 0) {
    return null;
  }
  
  // Extraer coordenadas
  const coordinates = successful.map(r => r.result!.coordinates);
  
  // Caso especial: un solo resultado
  if (coordinates.length === 1) {
    return {
      centroid: { ...coordinates[0] },
      radiusMeters: 0,
      concordantSources: 1,
      outlierSources: [],
      concordanceScore: 1 // Concordancia perfecta consigo mismo
    };
  }
  
  // Calcular centroide robusto
  const { centroid, weights } = huberCentroid(coordinates);
  
  // Identificar outliers
  const outlierSources = identifyOutliers(
    successful, 
    centroid, 
    weights, 
    concordanceThreshold
  );
  
  // Calcular radio del cluster (excluyendo outliers)
  const radiusMeters = calculateClusterRadius(coordinates, centroid, weights);
  
  // Calcular score de concordancia
  const concordanceScore = calculateConcordanceScore(successful, outlierSources);
  
  // Contar fuentes concordantes
  const concordantSources = successful.length - outlierSources.length;
  
  return {
    centroid,
    radiusMeters,
    concordantSources,
    outlierSources,
    concordanceScore
  };
}

// ============================================================================
// VALIDACIÓN CRUZADA COMPLETA
// ============================================================================

/**
 * Realiza la validación cruzada completa de una geocodificación.
 * 
 * Esta es la función principal que:
 * 1. Consulta múltiples fuentes en paralelo
 * 2. Analiza el cluster de resultados
 * 3. Calcula score compuesto
 * 4. Detecta discrepancias
 * 5. Genera recomendación
 * 
 * @param query - Query de geocodificación
 * @param sources - Fuentes a consultar
 * @param options - Opciones de configuración
 * @returns Resultado completo de validación cruzada
 * 
 * @example
 * const result = await performCrossValidation(
 *   { nombre: 'Centro de Salud', codMun: '04013', tipologia: 'SANITARIO' },
 *   [localSource, cartoCiudadSource, cdauSource]
 * );
 * 
 * if (result.recommendation === 'USE_RESULT') {
 *   console.log('Coordenada confiable:', result.recommendedCoordinate);
 * }
 */
export async function performCrossValidation(
  query: GeocodingQuery,
  sources: GeocodingSource[],
  options: {
    /** Timeout global en ms */
    timeoutMs?: number;
    /** Umbral de concordancia personalizado */
    concordanceThreshold?: number;
  } = {}
): Promise<CrossValidationResult> {
  const timestamp = new Date();
  
  // 1. Consultar múltiples fuentes en paralelo
  const sourceResults = await queryMultipleSources(query, sources, options.timeoutMs);
  
  // 2. Analizar cluster de resultados
  const clusterAnalysis = analyzeResultClusters(sourceResults, {
    concordanceThreshold: options.concordanceThreshold ?? getDiscrepancyThreshold(query.tipologia)
  });
  
  // Si no hay resultados exitosos — MJ-06: sin fuentes de contraste no hay evidencia
  // en contra de la coordenada; MANUAL_REVIEW en lugar de REJECT para no perder datos.
  // REJECT legítimo solo ocurre cuando hay ≥1 fuente activa con conflicto real.
  if (!clusterAnalysis) {
    return {
      query,
      sourceResults,
      clusterAnalysis: null,
      compositeScore: 0,
      recommendedCoordinate: null,
      recommendation: 'MANUAL_REVIEW',
      recommendationReason: 'Sin fuentes de contraste disponibles — coordenada no verificada pero no descartada',
      timestamp
    };
  }
  
  // 3. Calcular scores
  const successful = getSuccessfulResults(sourceResults);
  
  // Recolectar errores tipados para penalizaciones
  const collectedErrors: GeocodingError[] = sourceResults
    .filter(r => r.typedError !== null && r.typedError !== undefined)
    .map(r => r.typedError!);
  
  // Calcular matchScore promedio ponderado
  let totalMatchScore = 0;
  let totalWeight = 0;
  for (const r of successful) {
    const matchScore = r.result?.matchScore ?? 70; // Default 70 si no hay score
    totalMatchScore += matchScore * r.authorityWeight;
    totalWeight += r.authorityWeight;
  }
  const avgMatchScore = totalWeight > 0 ? totalMatchScore / totalWeight : 0;
  
  // F023 Fase 3.4: Calcular similaridad cruzada de nombres entre fuentes
  const crossNameScore = calculateCrossNameScore(sourceResults);
  
  // Bonus al matchScore si nombres entre fuentes son muy similares (>80%)
  const nameBonus = crossNameScore >= 80 ? (crossNameScore - 80) * 0.25 : 0;
  const adjustedMatchScore = Math.min(100, avgMatchScore + nameBonus);
  
  // Calcular authorityScore de fuentes concordantes
  const concordantResults = successful.filter(
    r => !clusterAnalysis.outlierSources.includes(r.sourceId)
  );
  const avgAuthority = concordantResults.length > 0
    ? concordantResults.reduce((sum, r) => sum + r.authorityWeight, 0) / concordantResults.length
    : 0;
  
  // Calcular score base
  const baseCompositeScore = calculateCompositeScore(
    adjustedMatchScore,
    clusterAnalysis.concordanceScore,
    avgAuthority
  );
  
  // Aplicar penalizaciones según errores encontrados
  const compositeScore = collectedErrors.length > 0
    ? applyErrorPenalties(baseCompositeScore, collectedErrors)
    : baseCompositeScore;
  
  // 4. Detectar discrepancias
  const discrepancy = detectDiscrepancy(clusterAnalysis, query.tipologia);
  
  // 4b. Si hay discrepancia, considerar usar centroide Huber del sistema tipado
  let finalCentroid = clusterAnalysis.centroid;
  let huberUsed = false;
  
  if (discrepancy.hasDiscrepancy && successful.length >= 2) {
    // Crear error de discrepancia tipado para obtener acción recomendada
    // TD-010.1: sourceId ya es canónico lowercase, uso directo
    const discrepancyContext = createErrorContext(successful[0].sourceId, DEFAULT_CROSS_VALIDATION_LEVEL, {
      elementName: query.nombre,
      municipio: query.municipio,
    });
    
    // Preparar datos para centroide Huber del sistema tipado
    const sourcesForHuber = successful.map(r => ({
      source: r.sourceId,
      coordinates: r.result!.coordinates,
      confidence: Math.round((r.result?.confidence ?? 0.7) * 100)
    }));
    
    const huberResult = huberCentroidFromSources(sourcesForHuber);
    
    // Crear error de discrepancia tipado con centroide Huber
    const discrepancyError = createError.sourceDiscrepancy(
      discrepancyContext,
      sourcesForHuber.map(s => ({
        source: s.source as GeocodingSourceId,
        coordinates: s.coordinates,
        confidence: s.confidence / 100
      })),
      clusterAnalysis.radiusMeters * 2, // maxDiscrepancy aproximado
      clusterAnalysis.radiusMeters,     // meanDiscrepancy aproximado
      huberResult.centroid,
      'accepted_huber'
    );
    
    const action = getRecommendedAction(discrepancyError);
    
    // Si la acción recomienda usar centroide Huber, aplicarlo
    if (action.action === 'use_huber_centroid') {
      finalCentroid = action.centroid;
      huberUsed = true;
    }
  }
  
  // 5. Generar recomendación
  const { recommendation, reason } = generateRecommendation(
    compositeScore,
    discrepancy,
    clusterAnalysis.concordantSources
  );
  
  // Enriquecer razón si se usó centroide Huber
  const finalReason = huberUsed 
    ? `${reason} (centroide Huber aplicado por discrepancia)`
    : reason;
  
  return {
    query,
    sourceResults,
    clusterAnalysis,
    compositeScore,
    recommendedCoordinate: finalCentroid,
    recommendation,
    recommendationReason: finalReason,
    timestamp
  };
}
